import { addDays, addMilliseconds, endOfToday, isSameDay, isToday, startOfToday } from 'date-fns'
import ru from 'date-fns/locale/ru'
import { utcToZonedTime, getTimezoneOffset } from 'date-fns-tz'
import { useRouter } from 'next/router'
import React from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import { ControllerRenderProps, FieldError, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Container, ErrorMessage, InputContainer, InputsContainer, Label } from './styled'
import { formatInitialDate } from '../../utils/formatInitialDate'

import 'react-datepicker/dist/react-datepicker.css'

registerLocale('ru', ru)

export const tranformCurrentTimezoneToNeeded = (date: Date, timezone: string) => {
  const currentOffset = -(new Date().getTimezoneOffset() * 60 * 1000)
  const neededTimezoneOffset = getTimezoneOffset(timezone)

  if (currentOffset === neededTimezoneOffset) {
    return date
  }

  const difference = currentOffset - neededTimezoneOffset
  return addMilliseconds(date, difference)
}

interface RangePickerProps extends ControllerRenderProps<FieldValues> {
  startLabel: string
  endLabel: string
  error?: FieldError
  disabled?: boolean
  preventPastDateSelection?: boolean
  dateFormat?: string
  showTimeSelect?: boolean
}

const RangePicker: React.FC<RangePickerProps> = ({
  startLabel,
  endLabel,
  error,
  value,
  onChange,
  disabled,
  preventPastDateSelection = true,
  dateFormat: dateFormatProp,
  showTimeSelect = true,
}) => {
  const { locale } = useRouter()
  const { t } = useTranslation('common')
  const [start, end] = value || []

  const parsedStart = start ? utcToZonedTime(start, 'Asia/Tbilisi') : undefined
  const parsedEnd = end ? utcToZonedTime(new Date(end), 'Asia/Tbilisi') : undefined
  const maxTime = endOfToday()

  const minDateStart = preventPastDateSelection
    ? utcToZonedTime(formatInitialDate(addDays(new Date(), 1)), 'Asia/Tbilisi')
    : undefined
  const minTimeStart = isToday(parsedStart)
    ? utcToZonedTime(new Date(), 'Asia/Tbilisi')
    : startOfToday()

  const minDateEnd = preventPastDateSelection ? addDays(parsedStart, 1) : undefined
  const minTimeEnd = isSameDay(parsedEnd, addDays(parsedStart, 1)) ? parsedStart : startOfToday()

  const pickerLocale = locale === 'ge' ? 'en' : locale
  let dateFormat = locale === 'ru' ? 'd MMM HH:mm' : 'd MMM h:mm aa'

  if (dateFormatProp) {
    dateFormat = dateFormatProp
  }

  return (
    <Container withError={!!error}>
      <InputsContainer>
        <InputContainer>
          <Label>{startLabel}</Label>
          <DatePicker
            disabledKeyboardNavigation
            minDate={minDateStart}
            minTime={minTimeStart}
            maxTime={maxTime}
            selected={parsedStart}
            onChange={(date) => {
              onChange({
                target: {
                  value: [tranformCurrentTimezoneToNeeded(date, 'Asia/Tbilisi').toISOString(), end],
                },
              })
            }}
            showTimeSelect={showTimeSelect}
            dateFormat={dateFormat}
            timeIntervals={60}
            disabled={disabled}
            locale={pickerLocale}
            timeCaption={t('time')}
          />
        </InputContainer>
        <InputContainer>
          <Label>{endLabel}</Label>
          <DatePicker
            disabledKeyboardNavigation
            selected={parsedEnd}
            onChange={(date) =>
              onChange({
                target: {
                  value: [
                    start,
                    tranformCurrentTimezoneToNeeded(date, 'Asia/Tbilisi').toISOString(),
                  ],
                },
              })
            }
            minDate={minDateEnd}
            minTime={minTimeEnd}
            maxTime={maxTime}
            showTimeSelect={showTimeSelect}
            dateFormat={dateFormat}
            timeIntervals={60}
            disabled={disabled}
            timeCaption={t('time')}
            locale={pickerLocale}
          />
        </InputContainer>
      </InputsContainer>
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
    </Container>
  )
}

export default RangePicker
